import { ApiResponse } from '@/domain';
import { Agreement } from '@/domain/agreements/agreement';
import { BillingType, InvoiceUserDataDto } from '@/domain/invoices/invoice';
import { AgreementCustomer, AgreementPayment, PaymentData, PaymentStatus, PaymentTransaction, UpdatePaymentTransactionData } from '@/domain/payments/payments';
import { InstallmentPayment } from '@/domain/subscriptions/installments';
import { backendApi } from '../backendApi';

export interface UpdateAgreementPaymentRequest {
  payment: UpdatePaymentTransactionData;
  invoice: InvoiceUserDataDto;
  interestAmount?: number;
}

export interface UpdateAgreementPaymentResponse {
  jobId: string
}

export const paymentsApi = {
  // Create, update, delete
  updatePayment: (paymentId: string, data: Partial<UpdatePaymentTransactionData>) =>
    backendApi.put<ApiResponse<PaymentTransaction>>(`/api/v1/payments/${paymentId}`, data).then((res) => res.data.data),

  updateAgreementPayment: (contractId: string, paymentId: string, data: Partial<UpdateAgreementPaymentRequest>) =>
    backendApi.put<ApiResponse<UpdateAgreementPaymentResponse>>(`/api/v1/orch/agreements/${contractId}/payments/${paymentId}`, data).then((res) => res.data.data),

  deleteAgreementPayment: (contractId: string, paymentId: string) =>
    backendApi.delete<ApiResponse<void>>(`/api/v1/orch/agreements/${contractId}/payments/${paymentId}`).then((res) => res.data.data),

  createMultiPayment: (paymentIds: string[]) =>
    backendApi.put<ApiResponse<{ payment: { id: string } }>>(`/api/v1/payments`, { paymentIds }).then((res) => res.data),

  deletePayment: (paymentId: string) =>
    backendApi.delete<ApiResponse<{ success: boolean }>>(`/api/v1/payments/${paymentId}`).then((res) => res.data.data),

  // Read
  getAgreementPayment: (contractId: string, paymentId: string) =>
    backendApi.get<ApiResponse<AgreementPayment[]>>(`/api/v1/data/agreements/${contractId}/payments/${paymentId}`).then((res) => res.data.data),

  getAgreementPayments: (contractId: string) =>
    backendApi.get<ApiResponse<PaymentData[]>>(`/api/v1/data/agreements/${contractId}/payments`).then((res) => res.data.data),

  getAgreementCustomer: (contractId: string) =>
    backendApi.get<ApiResponse<AgreementCustomer>>(`/api/v1/data/agreements/${contractId}/customer`).then((res) => res.data.data),

  getSubscriptionPayment: (billingPeriodId: string, paymentId: string) =>
    backendApi.get<ApiResponse<AgreementPayment[]>>(`/api/v1/data/billing-periods/${billingPeriodId}/payments/${paymentId}`).then((res) => res.data.data),

  getSubscriptionMultiPayment: (subscriptionId: string, paymentId: string) =>
    backendApi.get<ApiResponse<AgreementPayment[]>>(`/api/v1/data/subscriptions/${subscriptionId}/payments/${paymentId}`).then((res) => res.data.data),

  getInstallmentPayment: (contractId: string, paymentId: string) =>
    backendApi.get<ApiResponse<InstallmentPayment>>(`/api/v1/data/agreements/${contractId}/payments/${paymentId}/payment-plans`).then((res) => res.data.data),

  getAgreementByPayment: (paymentId: string) =>
    backendApi.get<ApiResponse<Agreement>>(`/api/v1/data/payments/${paymentId}/agreements`).then((res) => res.data.data),

  getMultiPaymentData: (paymentId: string) =>
    backendApi.get<ApiResponse<AgreementPayment[]>>(`/api/v1/data/payments/${paymentId}`).then((res) => res.data.data),

  getAgreementsByPayments: (paymentIds: string[]) =>
    backendApi.post<ApiResponse<Record<string, string>>>(`/api/v1/data/agreements/by-payments`, { paymentIds }).then((res) => res.data.data),

  invoicePayments: (paymentIds: string[], billingType: BillingType) =>
    backendApi.post<ApiResponse<{ jobId?: string }>>(`/api/v1/orch/invoices`, { paymentIds, billingType }).then((res) => res.data.data),

  refundPayments: (paymentIds: string[]) =>
    backendApi.post<ApiResponse<{ jobId?: string }>>(`/api/v1/orch/refunds`, { paymentIds }).then((res) => res.data.data),
};
